import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewEncapsulation,
} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {CartItem, ServiceOffer} from '../../../../data/model';
import {NavigationEventService} from '../../../navigation-event.service';
import {ServiceOfferFormComponent} from '../service-offer-form';
import {ContextData} from '../../../../common/context-data';

interface ServiceOfferSelectForm {
  addItemToCart: FormControl<boolean | null>;
  selectType: FormControl<string | null>;
  customAmount: FormControl<string | null>;
}

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-select-form',
  templateUrl: './select-form.component.html',
  styleUrl: './select-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SelectFormComponent extends ServiceOfferFormComponent {
  @Input() serviceOffer: ServiceOffer;
  @Input() contextData: ContextData;
  continueClicked = false;
  selectConfig = {
    label: 'Choose an amount',
  };

  customAmountConfig = {
    label: 'Enter amount',
    errorMessages: {
      min: 'Please enter any amount greater than $25.00',
      required: 'Please enter any amount greater than $25.00',
      pattern: 'Please enter a valid amount',
    },
  };

  showCustomAmount = false;
  selectOptions: SelectOption[] = [];

  offerForm: FormGroup<ServiceOfferSelectForm> =
    new FormGroup<ServiceOfferSelectForm>({
      addItemToCart: new FormControl<boolean | null>(null, []),
      selectType: new FormControl<string | null>('', {
        validators: [],
        nonNullable: false,
      }),
      customAmount: new FormControl<string | null>('', {
        validators: [],
        nonNullable: false,
      }),
    });

  constructor(
    protected ref: ChangeDetectorRef,
    private continueEvent: NavigationEventService,
  ) {
    super(ref);

    this.offerForm.get('selectType')?.valueChanges.subscribe(value => {
      this.showCustomAmount = value === 'Other';
      const cleanNumber = value && parseFloat(value?.replace(/[^0-9.-]+/g, ''));
      if (this.showCustomAmount) {
        this.offerForm.controls.customAmount.setValue('');
        this.offerForm.controls.customAmount.setValidators([
          Validators.required,
          Validators.min(25),
          Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
        ]);
      } else {
        this.offerForm.controls.customAmount.setValidators([]);
      }
      this.offerForm.controls.customAmount.updateValueAndValidity();
      if (cleanNumber && !Number.isNaN(cleanNumber)) {
        this.offerForm.controls.customAmount.setValue(cleanNumber.toString());
      }
      this.ref.detectChanges();
    });

    this.offerForm.get('customAmount')?.valueChanges.subscribe(value => {
      if (value && !Number.isNaN(value) && Number(value) > 0) {
        this.offerForm.controls.addItemToCart.setValue(true);
      }
    });
  }

  bindFormValue(selected: boolean, cartItem?: CartItem): void {
    if (!selected) {
      this.offerForm.reset();
      // Reset showCustomAmount and clear custom amount validators
      this.showCustomAmount = false;
      this.offerForm.controls.customAmount.setValidators([]);
      this.offerForm.controls.customAmount.updateValueAndValidity();

      // Explicitly set addItemToCart to false to ensure it's not accidentally set to true
      this.offerForm.controls.addItemToCart.setValue(false);
    } else {
      if (cartItem) {
        this.restoreFormFromCartItem(cartItem);
      }
      this.offerForm.controls.addItemToCart.setValue(true);
    }
    this.ref.detectChanges();
  }

  addonSelected(): boolean {
    return !!this.offerForm.value.addItemToCart;
  }

  value(): CartItem {
    // Use the custom amount if it exists and is valid, otherwise use the service offer's price
    const customAmount = this.offerForm.controls.customAmount.value;
    const unitPrice =
      customAmount &&
      !Number.isNaN(Number(customAmount)) &&
      Number(customAmount) > 0
        ? Number(customAmount)
        : this.serviceOffer?.quote?.cost || this.serviceOffer?.price || 0;

    const cartItem = super.value();
    cartItem.unitPrice = unitPrice;

    return cartItem;
  }

  /**
   * Gets the select options from the service offer's input labels in the xp object
   * @returns Array of select options with label and value
   */
  getSelectOptions(): SelectOption[] {
    if (!this.serviceOffer?.inputLabels?.length) {
      return [];
    }

    const options = this.serviceOffer.inputLabels.map(label => {
      return {
        label,
        value: label,
      };
    });

    return options;
  }

  restoreFormFromCartItem(cartItem: CartItem): void {
    this.offerForm.controls.addItemToCart.setValue(true);

    const options = this.getSelectOptions();
    const cartValue = cartItem.unitPrice;

    if (cartValue && cartValue > 0) {
      const matchingOption = options.find(option => {
        const optionValue = parseFloat(option.value.replace(/[^0-9.-]+/g, ''));
        return !isNaN(optionValue) && optionValue === cartValue;
      });

      if (matchingOption) {
        this.offerForm.controls.selectType.setValue(matchingOption.value);
        this.showCustomAmount = false;
      } else {
        this.offerForm.controls.selectType.setValue('Other');
        this.showCustomAmount = true;
        this.offerForm.controls.customAmount.setValue(cartValue.toString());
        this.offerForm.controls.customAmount.setValidators([
          Validators.required,
          Validators.min(25),
          Validators.pattern('^[0-9]+(.[0-9]{1,2})?$'),
        ]);
        this.offerForm.controls.customAmount.updateValueAndValidity();
      }
    }
  }
}
